import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Bot, Loader2, Mic, MicOff, Send, Sparkles, Wand2, Lightbulb } from 'lucide-react';
import {
  bidDomainApi,
  type BidDomainResult,
  type BidderInput,
  type SuggestedBidder,
} from '../lib/api/bidDomain';

import {
  formatDisplayNumber,
  formatNumberInput,
  parseNumberInput,
  parseNumberToFloat,
} from '../lib/numberFormat';
import { useSpeechToText } from '../lib/useSpeechToText';

const QUICK_PROMPTS = [
  '۴ رقیب دارم، قیمت‌های نمونه بساز و قیمت برد من را پیشنهاد بده',
  'فقط بهنگام را دارم — چه قیمتی بزنم تا برنده شوم؟',
  'قیمت من ۹۵٪ بهنگام است — در دامنه هستم؟',
  'سناریوی ۶ پیشنهاد با میانگین نزدیک بهنگام بساز',
];

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  suggestedBidders?: SuggestedBidder[];
  analysisPoints?: string[];
}

interface BidDomainAiPanelProps {
  guidelineId: number | '';
  Po: string;
  bidders: BidderInput[];
  result: BidDomainResult | null;
  projectId: number | '';
  projectName?: string;
  onApplyBidders: (bidders: BidderInput[], po?: string) => void;
  className?: string;
}

const BidDomainAiPanel: React.FC<BidDomainAiPanelProps> = ({
  guidelineId,
  Po,
  bidders,
  result,
  projectId,
  projectName,
  onApplyBidders,
  className = '',
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      role: 'assistant',
      content:
        'سلام! من دستیار تعیین دامنه هستم. سناریوی مناقصه را توضیح دهید — مثلاً تعداد رقبا، قیمت‌های تقریبی، یا فقط بهنگام — تا قیمت‌های نمونه و استراتژی برد پیشنهاد دهم.',
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastSuggested, setLastSuggested] = useState<SuggestedBidder[]>([]);
  const endRef = useRef<HTMLDivElement>(null);

  const handleVoiceTranscript = useCallback((text: string) => {
    setInput(text);
  }, []);

  const speech = useSpeechToText({
    lang: 'fa-IR',
    baseText: input,
    onFinalTranscript: handleVoiceTranscript,
  });

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, loading]);

  useEffect(() => {
    if (speech.error) setError(speech.error);
  }, [speech.error]);

  const parseBiddersForApi = () =>
    bidders
      .map((b) => ({
        name: b.name.trim() || undefined,
        price: parseNumberToFloat(b.price),
        is_self: b.is_self,
      }))
      .filter((b) => b.price > 0);

  const getEffectiveInput = useCallback(() => input.trim(), [input]);

  const sendMessage = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || loading) return;
    if (!guidelineId || !Po.trim()) {
      setError('ابتدا دستورالعمل و مبلغ بهنگام را وارد کنید.');
      return;
    }

    setError(null);
    setInput('');
    const userMsg: ChatMessage = { role: 'user', content: trimmed };
    setMessages((prev) => [...prev, userMsg]);
    setLoading(true);

    const conversation = [...messages, userMsg]
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .slice(-10)
      .map((m) => ({ role: m.role, content: m.content }));

    try {
      const res = await bidDomainApi.aiAssistant({
        guideline_id: Number(guidelineId),
        estimate_at_tender: parseNumberToFloat(Po),
        bidders: parseBiddersForApi(),
        result: result ?? undefined,
        message: trimmed,
        conversation: conversation.slice(0, -1),
        project_id: projectId !== '' ? Number(projectId) : undefined,
        project_name: projectName,
      });

      if (!res.success) {
        throw new Error(res.message ?? 'خطا در پاسخ دستیار');
      }

      const suggested = res.suggested_bidders ?? [];
      setLastSuggested(suggested);

      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: res.reply ?? '—',
          suggestedBidders: suggested.length > 0 ? suggested : undefined,
          analysisPoints: res.analysis_points,
        },
      ]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'خطا در ارتباط با دستیار');
    } finally {
      setLoading(false);
    }
  };

  const applySuggested = (suggested: SuggestedBidder[], po?: number | null) => {
    const mapped: BidderInput[] = suggested.map((b) => ({
      name: b.name,
      price: parseNumberInput(String(Math.round(b.price))),
      is_self: b.is_self,
    }));
    onApplyBidders(
      mapped,
      po != null ? parseNumberInput(String(Math.round(po))) : undefined
    );
  };

  return (
    <div className={`flex flex-col min-h-0 bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden ${className}`}>
      <div className="shrink-0 px-4 py-3 border-b border-slate-100 bg-gradient-to-l from-violet-50 to-white flex items-center gap-2">
        <div className="w-9 h-9 rounded-lg bg-violet-100 flex items-center justify-center">
          <Bot size={18} className="text-violet-700" />
        </div>
        <div>
          <h3 className="font-bold text-sm text-slate-900">دستیار هوشمند</h3>
          <p className="text-[11px] text-slate-500">تحلیل سناریو · پیشنهاد قیمت · راهنمای برد</p>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-3">
        {messages.map((m, i) => (
          <div
            key={i}
            className={`flex ${m.role === 'user' ? 'justify-start' : 'justify-end'}`}
          >
            <div
              className={`max-w-[95%] rounded-xl px-3 py-2.5 text-sm leading-relaxed ${
                m.role === 'user'
                  ? 'bg-emerald-700 text-white'
                  : 'bg-slate-50 border border-slate-200 text-slate-800'
              }`}
            >
              {m.role === 'assistant' && (
                <Sparkles size={12} className="inline-block ml-1 text-violet-500 mb-1" />
              )}
              <p className="whitespace-pre-wrap">{m.content}</p>

              {m.analysisPoints && m.analysisPoints.length > 0 && (
                <ul className="mt-2 space-y-1 text-xs text-slate-600">
                  {m.analysisPoints.map((pt, j) => (
                    <li key={j} className="flex gap-1.5">
                      <Lightbulb size={12} className="shrink-0 text-amber-500 mt-0.5" />
                      {pt}
                    </li>
                  ))}
                </ul>
              )}

              {m.suggestedBidders && m.suggestedBidders.length > 0 && (
                <div className="mt-3 pt-2 border-t border-slate-200/80">
                  <p className="text-[11px] font-bold text-violet-800 mb-1.5">قیمت‌های پیشنهادی</p>
                  <div className="space-y-1">
                    {m.suggestedBidders.map((b, j) => (
                      <div key={j} className="flex justify-between text-xs gap-2">
                        <span className={b.is_self ? 'font-bold text-emerald-700' : ''}>
                          {b.name || (b.is_self ? 'قیمت من' : `پیشنهاد ${j + 1}`)}
                          {b.is_self ? ' ★' : ''}
                        </span>
                        <span className="font-mono">{formatDisplayNumber(b.price)}</span>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => applySuggested(m.suggestedBidders!)}
                    className="mt-2 w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-600 text-white text-xs font-bold hover:bg-violet-700"
                  >
                    <Wand2 size={14} />
                    اعمال در فرم
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex justify-end">
            <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 flex items-center gap-2 text-sm text-slate-600">
              <Loader2 size={16} className="animate-spin text-violet-600" />
              در حال تحلیل...
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      <div className="shrink-0 border-t border-slate-100 p-3 space-y-2 bg-slate-50/50">
        {error && (
          <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-2 py-1.5">{error}</p>
        )}

        <div className="flex flex-wrap gap-1.5">
          {QUICK_PROMPTS.map((q) => (
            <button
              key={q}
              type="button"
              onClick={() => sendMessage(q)}
              disabled={loading}
              className="text-[10px] px-2 py-1 rounded-full border border-violet-200 bg-white text-violet-800 hover:bg-violet-50 disabled:opacity-50"
            >
              {q.length > 42 ? `${q.slice(0, 40)}…` : q}
            </button>
          ))}
        </div>

        {lastSuggested.length > 0 && (
          <button
            type="button"
            onClick={() => applySuggested(lastSuggested)}
            className="w-full text-xs font-bold text-violet-700 flex items-center justify-center gap-1 py-1.5 hover:underline"
          >
            <Wand2 size={14} />
            اعمال آخرین قیمت‌های پیشنهادی
          </button>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (speech.listening) speech.stop();
            sendMessage(getEffectiveInput());
          }}
          className="space-y-2"
        >
          {speech.listening && (
            <div className="flex items-center gap-2 text-xs text-violet-700 bg-violet-50 border border-violet-200 rounded-lg px-2.5 py-1.5">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
              </span>
              در حال ضبط… برای پایان دوباره روی میکروفون بزنید
            </div>
          )}
          {speech.transcribing && (
            <div className="flex items-center gap-2 text-xs text-violet-700 bg-violet-50 border border-violet-200 rounded-lg px-2.5 py-1.5">
              <Loader2 size={14} className="animate-spin" />
              در حال تبدیل گفتار به متن...
            </div>
          )}

          <div className="flex gap-2">
            <textarea
              value={input}
              onChange={(e) => {
                if (speech.listening) speech.stop();
                setInput(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (speech.listening) speech.stop();
                  sendMessage(getEffectiveInput());
                }
              }}
              rows={2}
              disabled={speech.transcribing}
              placeholder={
                speech.supported
                  ? 'سناریو را بنویسید یا با میکروفون ضبط کنید...'
                  : 'سناریو را توضیح دهید... (Enter ارسال)'
              }
              className="flex-1 min-w-0 px-3 py-2 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-violet-300/50 resize-none disabled:opacity-60"
            />
            <div className="flex flex-col gap-1.5 self-end">
              {speech.supported && (
                <button
                  type="button"
                  onClick={speech.toggle}
                  disabled={loading || speech.transcribing}
                  title={speech.listening ? 'توقف ضبط' : 'ضبط صدا'}
                  className={`shrink-0 px-3 py-2 rounded-lg border disabled:opacity-50 ${
                    speech.listening
                      ? 'bg-red-50 border-red-300 text-red-600 hover:bg-red-100'
                      : 'bg-white border-slate-200 text-slate-600 hover:bg-violet-50 hover:border-violet-300 hover:text-violet-700'
                  }`}
                >
                  {speech.listening ? <MicOff size={18} /> : <Mic size={18} />}
                </button>
              )}
              <button
                type="submit"
                disabled={loading || speech.transcribing || !getEffectiveInput()}
                className="shrink-0 px-3 py-2 rounded-lg bg-violet-600 text-white disabled:opacity-50 hover:bg-violet-700"
              >
                <Send size={18} />
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BidDomainAiPanel;
